// Farming (M11.1). PURE (ENG-1): the crop table + growth/hydration/harvest rules
// the host drives from random ticks and interactions. Crops advance an `age`
// property from 0 to maturity; growth is probabilistic per random tick and faster
// on hydrated, well-lit farmland (the classic Minecraft point model).

import type { Gene } from '../registry/schemas';

export interface CropDef {
  /** Heredity gene table (HER-8, ARCH §28.3) — wheat and beetroots in v1 (M50.1).
   *  Absent ⇒ a plain crop on every path. */
  genes?: Gene[];
  /** Crop block name (e.g. `wheat`). */
  block: string;
  /** Age at maturity (wheat/carrot/potato 7, beetroot 3). */
  maxAge: number;
  /** Seed item planted onto farmland to create age 0. */
  seed: string;
  /** Produce item yielded when a mature crop is harvested. */
  produce: string;
  /** Produce count at maturity. */
  produceMax: number;
  /** Extra seeds yielded at maturity (0..this, deterministic-rolled). */
  seedBonus: number;
}

// Heredity's v1 crop gene table (HER-8, ARCH §28.3): five polygenic genes, no
// Mendelian genes, 5 bytes — identical shape on wheat and beetroots, so a
// genome decodes the same way regardless of which of the two it came from.
const CROP_GENES: Gene[] = [
  { name: 'growth', kind: 'polygenic', expresses: { scalar: 'growthMul', range: [0.7, 1.3] } },
  { name: 'yield', kind: 'polygenic', expresses: { scalar: 'yieldMul', range: [0.6, 1.6] } },
  { name: 'seed_bonus', kind: 'polygenic', expresses: { scalar: 'seedBonus', range: [0, 3] } },
  { name: 'drought', kind: 'polygenic', expresses: { scalar: 'droughtTol', range: [0, 1] } },
  { name: 'stalk_colour', kind: 'polygenic', expresses: { scalar: 'stalkQ', range: [0, 1] } },
];

/** Data describes; this table names the crops the code grows (BLK-4-adjacent). */
export const CROPS: Record<string, CropDef> = {
  wheat: { block: 'wheat', maxAge: 7, seed: 'wheat_seeds', produce: 'wheat', produceMax: 1, seedBonus: 3, genes: CROP_GENES },
  carrots: { block: 'carrots', maxAge: 7, seed: 'carrot', produce: 'carrot', produceMax: 3, seedBonus: 0 },
  potatoes: { block: 'potatoes', maxAge: 7, seed: 'potato', produce: 'potato', produceMax: 3, seedBonus: 0 },
  beetroots: { block: 'beetroots', maxAge: 3, seed: 'beetroot_seeds', produce: 'beetroot', produceMax: 1, seedBonus: 3, genes: CROP_GENES },
  // The sniffer's finds (M19): torchflower seeds → a torchflower flower; pitcher pods
  // → a pitcher plant. (Key MUST equal `block` — cropOf looks up CROPS by block name.)
  torchflower_crop: { block: 'torchflower_crop', maxAge: 2, seed: 'torchflower_seeds', produce: 'torchflower', produceMax: 1, seedBonus: 1 },
  pitcher_crop: { block: 'pitcher_crop', maxAge: 4, seed: 'pitcher_pod', produce: 'pitcher_plant', produceMax: 1, seedBonus: 0 },
  // Cotton (fibre): the renewable path to string, so a peaceful player can
  // craft a bow/fishing rod/lead without farming spiders for drops.
  cotton: { block: 'cotton', maxAge: 7, seed: 'cotton_seeds', produce: 'cotton', produceMax: 2, seedBonus: 2 },
};

/** Seed items that plant a crop, mapped to the crop block. */
export const SEED_TO_CROP: Record<string, string> = Object.fromEntries(
  Object.values(CROPS).map((c) => [c.seed, c.block]),
);

export function cropOf(block: string): CropDef | undefined {
  return CROPS[block];
}

export function isMature(crop: CropDef, age: number): boolean {
  return age >= crop.maxAge;
}

/** The base per-random-tick growth chance for a moisture level (PLR farming):
 *  hydrated farmland (moisture > 0) roughly doubles the dry chance. Exported so
 *  a genetic plant's `growthMul` (HER-8, ARCH §28.10) can scale the same number
 *  `shouldGrow` uses internally. */
export function growthChance(moisture: number): number {
  return moisture > 0 ? 0.5 : 0.25;
}

/**
 * Growth chance per random tick (PLR farming). Minecraft accumulates "points"
 * from the soil under and around the crop: dry farmland gives fewer points than
 * hydrated. We collapse that to a per-tick probability that is higher when the
 * farmland below is moist. Returns true if the crop should advance one age.
 */
export function shouldGrow(moisture: number, roll: number): boolean {
  return roll < growthChance(moisture);
}

/** Bonemeal advances a crop by this many ages (Minecraft 2–5, seeded). */
export function bonemealGrowth(roll: number): number {
  return 2 + Math.floor(roll * 4); // 2..5
}

/** Farmland moisture after a random tick: 7 when water is in range, else dries. */
export function nextMoisture(current: number, waterNearby: boolean): number {
  if (waterNearby) return 7;
  return Math.max(0, current - 1);
}

/** Compostables and the fill chance they add to a composter (M11.1). */
export const COMPOSTABLES: Record<string, number> = {
  wheat_seeds: 0.3, beetroot_seeds: 0.3, wheat: 0.65, carrot: 0.65, potato: 0.65, beetroot: 0.65,
  poppy: 0.65, dandelion: 0.65, tall_grass: 0.3, sugar_cane: 0.5, cactus: 0.5, oak_leaves: 0.3,
};
